import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { showNotification, useQueryFetch } from 'sonata-react-components';
import { getInsertionOrder } from 'sonata-static-storage/lib/entities';

import { GlobalCatch } from '@/components/common/GlobalCatch';
import FormProviderWrapper from '@/components/ioSettings/InsertionOrderForm/FormProviderWrapper/FormProviderWrapper.tsx';
import { Skeleton } from '@/components/ioSettings/Skeleton';
import { useDoohAudience } from '@/hooks/useDoohAudience';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { createStore, Provider } from '@/store/local/ioSettings';
import { IOWizardLayout } from '../common/entityLayout';

export const Catch = () => {
  return <GlobalCatch />;
};

export default function _layout() {
  const { formatMessage } = useIntl();
  const { pathname, state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const userEntity = useGlobalStore(getUser);
  const isCreate = pathname.includes('create');
  const isValidCreate = isCreate && Boolean(state?.campaignPlan);
  const isEdit = pathname.includes('edit');
  const isValidEdit = isEdit && Boolean(id);

  useEffect(() => {
    if ((isCreate && !isValidCreate) || (isEdit && !isValidEdit) || (!isCreate && !isEdit)) {
      showNotification(
        'warning',
        formatMessage({
          defaultMessage:
            'Looks like this insertion order page is no longer available. Taking you back to your line items…',
          id: 'insertionOrders.errorInvalidAccess'
        })
      );
      navigate('/');
    }
  }, []);

  const {
    data: insertionOrder,
    errorMsg: insertionOrderErrorMsg,
    isFetching: ioIsFetching
  } = useQueryFetch(['ioSettings', id], () => getInsertionOrder(userEntity.get('id')!, id!, {}), {
    enabled: isValidEdit && !!id,
    staleTime: 0
  });

  const { doohAudience, isFetching: doohAudienceIsFetching } = useDoohAudience(
    'insertionOrder',
    isValidEdit ? id : undefined,
    insertionOrder?.toJSON().type
  );

  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!insertionOrder && isValidEdit && !ioIsFetching && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      if (insertionOrderErrorMsg === 'Not Found') {
        showNotification(
          'warning',
          formatMessage({
            defaultMessage: 'Looks like this insertion order no longer exists. Taking you back to your campaign plans…',
            id: 'insertionOrders.errorNotFound'
          })
        );
      } else {
        showNotification(
          'warning',
          formatMessage({
            defaultMessage:
              'We couldn’t fetch the insertion order due to a connection or server error. Redirecting to your campaign plans…',
            id: 'insertionOrders.errorFetch'
          })
        );
      }
      navigate('/');
    }
  }, [insertionOrder, isValidEdit, ioIsFetching, insertionOrderErrorMsg]);

  if (ioIsFetching || doohAudienceIsFetching) {
    return <Skeleton />;
  }

  if (!insertionOrder && isValidEdit) {
    return null;
  }

  const currentInsertionOrder = isValidEdit && insertionOrder ? insertionOrder.toJSON() : undefined;

  return (
    <Provider createStore={() => createStore(currentInsertionOrder, state?.campaignPlan, userEntity, { doohAudience })}>
      <FormProviderWrapper>
        <IOWizardLayout entityType="insertionOrder" />
      </FormProviderWrapper>
    </Provider>
  );
}
